import { Component, ErrorInfo, ReactNode } from 'react';

import { datadogLogger } from '../../datadog/logger';

import { ErrorBoundaryFallback } from './ErrorBoundaryFallback';

type Props = {
    children: ReactNode;
    fallback?: (reset: () => void) => ReactNode;
};

type State = {
    hasError: boolean;
};

export class AppErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        datadogLogger.error('[ErrorBoundary] Unhandled rendering error', {
            error_name: error.name,
            error_message: error.message,
            component_stack: info.componentStack,
        });
    }

    private handleReset = (): void => {
        this.setState({ hasError: false });
    };

    render(): ReactNode {
        if (this.state.hasError) {
            return (
                this.props.fallback?.(this.handleReset) ?? (
                    <ErrorBoundaryFallback onReset={this.handleReset} />
                )
            );
        }

        return this.props.children;
    }
}
